import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// P4-B — Locales lazy-loaded para reduzir boot mobile.
// Antes: ~285 KB de JSON (es + en + pt-BR) empacotados no chunk LocaleContext.
// Depois: só o locale ativo entra no boot; outros carregam sob demanda em
// setLocale via dynamic import (~1 chunk por locale).
import es from './locales/es.json';

// Language to currency mapping
export const LOCALE_CONFIG = {
  es: {
    language: 'es',
    currency: 'EUR',
    currencySymbol: '€',
    currencyLocale: 'es-ES',
    name: 'Español',
    flag: '🇪🇸',
    subdomain: 'es'
  },
  'pt-BR': {
    language: 'pt-BR',
    currency: 'BRL',
    currencySymbol: 'R$',
    currencyLocale: 'pt-BR',
    name: 'Português',
    flag: '🇧🇷',
    subdomain: 'br'
  },
  en: {
    language: 'en',
    currency: 'USD',
    currencySymbol: '$',
    currencyLocale: 'en-US',
    name: 'English (USD)',
    flag: '🇺🇸',
    subdomain: 'en'
  },
  'en-EU': {
    language: 'en',
    currency: 'EUR',
    currencySymbol: '€',
    currencyLocale: 'en-GB',
    name: 'English (EUR)',
    flag: '🇬🇧',
    subdomain: 'eu'
  }
} as const;

export type LocaleKey = keyof typeof LOCALE_CONFIG;

export const normalizeLocaleKey = (lng: string | null | undefined): LocaleKey => {
  if (!lng) return 'es';
  const lower = lng.toLowerCase();

  if (lower === 'ptbr' || lower === 'pt_br' || lower.startsWith('pt-')) return 'pt-BR';
  if (lower === 'en-us' || lower === 'en_us') return 'en';
  if (lower === 'en-eu' || lower === 'en_eu' || lower === 'en-gb' || lower === 'en_gb') return 'en-EU';
  if (lower === 'es-es' || lower === 'es_es' || lower.startsWith('es-')) return 'es';

  if (lng in LOCALE_CONFIG) return lng as LocaleKey;

  if (lower.startsWith('pt')) return 'pt-BR';
  if (lower.startsWith('en')) return 'en';
  return 'es';
};

// Detect language from subdomain or TLD
const detectSubdomainLanguage = (): LocaleKey | null => {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];

  if (subdomain === 'en' || subdomain === 'us') return 'en';
  if (subdomain === 'eu' || subdomain === 'en-eu') return 'en-EU';
  if (subdomain === 'es') return 'es';
  if (subdomain === 'br' || subdomain === 'pt-br') return 'pt-BR';

  if (hostname.endsWith('.com.br')) return 'pt-BR';
  if (subdomain === 'www' && hostname.endsWith('.com') && !hostname.endsWith('.com.br')) return 'es';

  return null;
};

const customSubdomainDetector = {
  name: 'customSubdomain',
  lookup: () => detectSubdomainLanguage(),
  cacheUserLanguage: () => {}
};

// SSR sempre renderiza em 'es' (window/localStorage indisponíveis). Para
// evitar hydration mismatch (#418/#422/#425), o cliente TAMBÉM inicia em 'es'
// e a LocaleContext troca para o locale detectado dentro de useEffect, após
// a hidratação. Detecção real permanece exposta para uso pós-mount.
export const detectClientLocale = (): LocaleKey => {
  const subdomainLang = detectSubdomainLanguage();
  if (subdomainLang) return subdomainLang;

  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('i18nextLng');
    if (stored) return normalizeLocaleKey(stored);
  }

  if (typeof navigator !== 'undefined') {
    return normalizeLocaleKey(navigator.language);
  }
  return 'es';
};

const getI18nLanguage = (locale: LocaleKey): string => {
  return locale === 'en-EU' ? 'en' : locale;
};

// Sempre 'es' na inicialização do módulo — garante que SSR e primeira
// hidratação renderizem o mesmo HTML.
export const initialLocale: LocaleKey = 'es';
const initialI18nLng = getI18nLanguage(initialLocale);

const languageDetector = new LanguageDetector();
languageDetector.addDetector(customSubdomainDetector);

// Boot com apenas o locale ativo. Fallback 'es' embutido só se for o inicial;
// caso contrário, quando o browser precisar fallback, ele pega o bundle já carregado.
// 'es' sempre embutida — é o fallback do SSR (window undefined → locale='es'),
// então precisa estar disponível na hidratação. Outros locales viram chunks.
const initialResources: Record<string, { translation: unknown }> = {
  es: { translation: es },
};

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: initialResources,
    lng: initialI18nLng,
    fallbackLng: 'es',
    interpolation: { escapeValue: false },
    detection: {
      order: ['customSubdomain', 'localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

// Dynamic loader via fetch(?url). Usamos `?url` para que o Vite emita cada
// JSON como asset separado SEM gerar <link rel="modulepreload">. Assim, ao
// bootar em espanhol, os bundles en/pt-BR não são baixados junto no HTML —
// só quando o usuário trocar de idioma.
import ptBrUrl from './locales/pt-BR.json?url';
import enUrl from './locales/en.json?url';
import esUrl from './locales/es.json?url';

const LOCALE_URLS: Record<string, string> = {
  'pt-BR': ptBrUrl,
  en: enUrl,
  es: esUrl,
};

const loadedLangs = new Set<string>(['es']);
export const ensureLanguageLoaded = async (lng: string): Promise<void> => {
  if (loadedLangs.has(lng)) return;
  const url = LOCALE_URLS[lng];
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const data = await res.json();
    i18n.addResourceBundle(lng, 'translation', data, true, true);
    loadedLangs.add(lng);
  } catch (err) {
    console.error('Failed to load locale', lng, err);
  }
};

// Detecção e carregamento do locale real são feitos pela LocaleProvider em
// useEffect (após hidratação), evitando divergência SSR/CSR.

export default i18n;
